import { ObjectListRequestType } from '../ObjectListResponseTypes'
import { ObjectListType } from '../ObjectListTypes'

// Use a unique name for your local Subscription interface to avoid conflicts
export interface SubscriptionListType extends ObjectListType {
  subscriptions: SubscriptionType[]
}

export interface SubscriptionType {
  id: number
  type: string
  desc: string
  statusId: number
  status: string
  product: SubscriptionTypeMobile | SubscriptionTypeInternet | undefined
}

export interface SubscriptionTypeProduct {
  id: number
  saleId: number
}

export interface SubscriptionTypeMobile extends SubscriptionTypeProduct {
  telephoneNumber: string
  label: string
  simCardNumber: string
  simType: string
  imsiNumber: string
  puk1: string
  puk2: string
  activateMms: number
  blockInternational: number
  blockVas: number
  outOfBundleSms: number
  dailyBLockingLimit: number
  firstOutOfBundleLimit: number
  monthlyBlockingLimit: number
  monthlyWarningLimit: number
  secondOutOfBundleLimit: number
  statusId: number
  status: string
  bundle: MobileProductBundle
  editable: number
  price: number
  outOfBundleCost: number
  activationDate: string | null
  terminationDate: string | null
  isDeliveryContact: boolean
}

export interface MobileProductBundle {
  data: number
  voice: number
  sms: number
}

export interface SubscriptionTypeInternet extends SubscriptionTypeProduct {
  address: SubscriptionAddress
  circuitId: string
  label: string
  lex: string
  utac: string
  uni: string
  userName: string
  passWord: string
  fixedIp: number
  fixedIpAddress: string
  useIpV6: number
  statusId: number
  status: string
  connectionType: string
}

export interface SubscriptionAddress {
  street: string
  streetNumber: string
  streetNumberAlpha: string
  bus: string
  zipCode: number
  city: string
  countryId: number
  country: string
}

export interface SubscriptionStatusCountGroup {
  active: number
  inactive: number
  terminated: number
}

export interface SubscriptionStatusCounts {
  internet: SubscriptionStatusCountGroup
  mobile: SubscriptionStatusCountGroup
}

export interface SubscriptionListRequest extends ObjectListRequestType {}

export const isSubscription = (item: any): item is SubscriptionType => {
  // A valid, single Subscription must be an object, not an array,
  // and have a 'product' property.
  if (item === undefined || item === null || Array.isArray(item)) {
    return false
  }
  return typeof item === 'object' && 'product' in item
}

export const isSubscriptionList = (subscription: any): subscription is SubscriptionListType => {
  if (subscription === undefined) {
    return false
  }
  return 'subscriptions' in subscription
}

export const isOfSubscriptionType = (
  subscription: any,
  subscriptiontype: string,
): subscription is SubscriptionListType => {
  if (subscription === undefined) {
    return false
  }
  if (!isSubscriptionList(subscription)) {
    return false
  }
  if (subscription.subscriptions.length > 0) {
    return subscription.subscriptions[0].type == subscriptiontype
  }
  return false
}

export const isSubscriptionRequest = (request: any): request is SubscriptionListRequest => {
  if (request === undefined) {
    return false
  }
  return 'subscriptiontype' in request
}

export const isSubscriptionTypeMobile = (data: any): data is SubscriptionTypeMobile => {
  if (data === undefined) {
    return false
  }
  return 'telephoneNumber' in data
}

export const isSubscriptionTypeInternet = (data: any): data is SubscriptionTypeInternet => {
  if (data === undefined) {
    return false
  }
  return 'address' in data
}

export interface ProductCounts {
  active: number
  inactive: number
  terminated: number
}

// Represents the main configuration for all product types
export interface ProductConfig {
  [key: string]: {
    data: SubscriptionType[]
    statuses: {
      active: number[]
      inactive: number[]
      terminated: number[]
    }
  }
}

// Represents the final computed product counts object
export interface FinalProductCounts {
  [key: string]: ProductCounts
}

/**
 * Enum for every specific status ID provided.
 */
export enum SubscriptionStatusId {
  Created = 10,
  WaitingForOrderApproval = 20,
  WaitingForFulfillmentInitiation = 22,
  WaitingForFulfillmentCompletion = 26,
  Fulfilled = 30, // Active
  Ended = 35, // Terminated
  OrderCancelled = 37,
  OrderRejected = 38,
  QuotationRejected = 39,
  WaitingForCleanupApproval = 40,
  WaitingForCleanupInitiation = 42,
  WaitingForCleanupCompletion = 46,
  Removed = 50, // Terminated
  FulfillmentError = 99,
}

/**
 * Open = pending or active (up to Fulfilled), or stuck in FulfillmentError.
 * Everything else is ended or being cleaned up.
 */
export const isOpenSubscriptionStatus = (statusId: number): boolean => {
  return statusId <= SubscriptionStatusId.Fulfilled || statusId === SubscriptionStatusId.FulfillmentError
}

/**
 * Groupings for filters (Active, Inactive, Terminated).
 * These arrays are what you pass to your Filter logic.
 */
export const SubscriptionStatusGroup = {
  Active: [SubscriptionStatusId.Fulfilled],

  Terminated: [
    SubscriptionStatusId.Ended,
    SubscriptionStatusId.Removed,
    SubscriptionStatusId.OrderCancelled,
    SubscriptionStatusId.OrderRejected,
    SubscriptionStatusId.WaitingForCleanupApproval,
    SubscriptionStatusId.WaitingForCleanupInitiation,
    SubscriptionStatusId.WaitingForCleanupCompletion,
  ],

  Inactive: [
    SubscriptionStatusId.WaitingForFulfillmentInitiation,
    SubscriptionStatusId.WaitingForFulfillmentCompletion,
    SubscriptionStatusId.Created,
    SubscriptionStatusId.WaitingForOrderApproval,
    SubscriptionStatusId.QuotationRejected,
  ],

  Hidden: [],
} as const

/**
 * Helper to get the TRANSLATION KEY for a status group.
 */
export const getStatusGroupLabelKey = (groupKey: keyof typeof SubscriptionStatusGroup): string => {
  // Just return the key string
  return `products.status_group.${groupKey}`
}

/**
 * Helper to get the TRANSLATION KEY for a status.
 */
export const getStatusLabelKey = (statusKey: keyof typeof SubscriptionStatusId): string => {
  // Just return the key string
  return `products.status.${statusKey}`
}
